import moment from "moment";
import {useNavigate} from "react-router-dom";

import Page from "@/components/Page";
import {
	DsForm,
	DsHeader,
	DsKpi,
	DsKpiRow,
	DsMissingSources,
	DsResultWarnings,
	DsSection,
	DsStatus,
	DsTable,
	DsSkeleton,
} from "@/components/DsKit";

import {useClubSnapshot} from "@/utils/hooks";
import {
	DEPARTMENTS,
	summarizeDsAge,
	matchDateLabel,
	matchCountdown,
} from "@/utils/clubSnapshot";

const depTone = (count, min) =>
	count >= min ? "ok" : count >= Math.ceil(min / 2) ? "warn" : "alert";

function DsTeamsPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();
	const {
		data: s,
		loading,
		reload,
		scope,
	} = useClubSnapshot({handleNotification});

	const teams = s?.teams || [];
	const age = summarizeDsAge(teams.flatMap((t) => t.players));

	const columns = [
		{
			key: "name",
			label: "Squadra",
			render: (t) => (
				<>
					<div className="ds-strong">{t.name}</div>
					{t.category && t.category !== t.name && (
						<div className="ds-muted">{t.category}</div>
					)}
				</>
			),
		},
		{
			key: "available",
			label: "Senza infortunio attivo",
			align: "center",
			render: (t) => (
				<DsStatus
					status={
						!t.players.length
							? "none"
							: t.injured.length / t.players.length > 0.15
								? "alert"
								: t.injured.length
									? "warn"
									: "ok"
					}
					label={`${t.available}/${t.players.length}`}
				/>
			),
		},
		{
			key: "age",
			label: "Età media",
			align: "center",
			render: (t) => (t.avgAge !== null ? t.avgAge.toFixed(1) : "—"),
		},
		...DEPARTMENTS.map((d) => ({
			key: d.id,
			label: d.label,
			align: "center",
			render: (t) => {
				const dep = t.departments[d.id];
				return (
					<span
						className={"ds-dep ds-dep--" + depTone(dep.available, d.min)}
						title={`${dep.available} disponibili su ${dep.total} — minimo ${d.min}`}
					>
						{dep.available}
					</span>
				);
			},
		})),
		{
			key: "record",
			label: "Vinte · pari · perse",
			align: "center",
			render: (t) => `${t.record.V} · ${t.record.N} · ${t.record.P}`,
		},
		{
			key: "form",
			label: "Ultime 5",
			render: (t) => <DsForm form={t.form} />,
		},
		{
			key: "next",
			label: "Prossima gara",
			render: (t) =>
				t.nextMatch ? (
					<>
						<div>
							{t.nextMatch.homeClub} — {t.nextMatch.awayClub}
						</div>
						<div className="ds-muted">
							{matchCountdown(t.nextMatch)} · {matchDateLabel(t.nextMatch)}
						</div>
					</>
				) : (
					<span className="ds-muted">—</span>
				),
		},
		{
			key: "contracts",
			label: "Scadenze",
			align: "center",
			render: (t) => {
				const n = t.contracts.filter((c) => {
					const days = moment(c.endDate).diff(moment(), "days");
					return days >= 0 && days <= 180;
				}).length;
				return n ? (
					<DsStatus status="warn" label={n} />
				) : (
					<span className="ds-muted">0</span>
				);
			},
		},
		{
			key: "staff",
			label: "Staff",
			align: "center",
			render: (t) =>
				t.staff.length ? t.staff.length : <DsStatus status="alert" label="0" />,
		},
	];

	return (
		<Page>
			<div className="ds">
				<DsHeader
					title="Squadre"
					subtitle="Lo stato di ogni squadra a colpo d'occhio"
					snapshot={s}
					scope={scope}
					area="squadre"
					onReload={reload}
					reloading={loading}
				/>
				<DsSkeleton visible={loading && !s} />
				{s && (
					<>
						<DsMissingSources sources={s.missingSources} />
						<DsResultWarnings teams={s.teams} />
						<DsKpiRow>
							<DsKpi label="Squadre" value={teams.length} />
							<DsKpi
								label="Giocatori unici"
								value={s.totalPlayers}
								sub={s.membershipCount + " appartenenze nelle rose selezionate"}
							/>
							<DsKpi
								label={teams.length === 1 ? "Età media rosa" : "Età media club"}
								value={age.value === null ? "—" : age.value.toFixed(1)}
								sub={`anni · ${age.known}/${age.total} giocatori unici con data di nascita`}
							/>
							<DsKpi
								label="Bilancio delle partecipazioni"
								value={`${teams.reduce((n, t) => n + t.record.V, 0)}V`}
								sub={`${teams.reduce((n, t) => n + t.record.N, 0)} pareggi · ${teams.reduce((n, t) => n + t.record.P, 0)} sconfitte`}
							/>
						</DsKpiRow>
						<DsSection
							title="Quadro squadre"
							hint="Giocatori disponibili per reparto: verde se sopra il minimo consigliato. Clicca una squadra per la sua sintesi."
						>
							<DsTable
								columns={columns}
								rows={teams}
								onRowClick={(t) => navigate("/ds/squadra/" + t.id)}
								empty="Nessuna squadra per la stagione selezionata."
							/>
						</DsSection>
					</>
				)}
			</div>
		</Page>
	);
}

export default DsTeamsPage;
